"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { ChevronDown, Play, ArrowRight } from "lucide-react";

export default function Hero() {
  const [currentText, setCurrentText] = useState(0);

  const texts = [
    "Mobile Apps",
    "iOS Development",
    "Android Development",
    "Cross-Platform Solutions",
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentText((prev) => (prev + 1) % texts.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [texts.length]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary-50 via-white to-accent-50"></div>

      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-accent-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse animation-delay-2000"></div>
        <div className="absolute top-40 left-1/2 w-80 h-80 bg-primary-300 rounded-full mix-blend-multiply filter blur-xl opacity-50 animate-pulse animation-delay-4000"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center">
          {/* Main heading */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-secondary-900 mb-6 animate-fade-in">
            We Build Amazing
            <span className="block text-primary-600 mt-2">
              <span key={currentText} className="inline-block animate-slide-up">
                {texts[currentText]}
              </span>
            </span>
          </h1>

          {/* Subtitle */}
          <p className="text-xl text-secondary-600 mb-8 max-w-3xl mx-auto animate-slide-up">
            Based in Atlanta, we specialize in creating innovative mobile
            applications that deliver exceptional user experiences and drive
            business growth.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12 animate-slide-up">
            <Link
              href="#contact"
              className="btn-primary flex items-center space-x-2"
            >
              <span>Start Your Project</span>
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              href="#portfolio"
              className="btn-secondary flex items-center space-x-2"
            >
              <Play className="w-5 h-5" />
              <span>View Our Work</span>
            </Link>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-2xl mx-auto animate-slide-up">
            <div className="text-center">
              <div className="text-3xl font-bold text-primary-600 mb-2">
                50+
              </div>
              <div className="text-secondary-600">Apps Delivered</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-primary-600 mb-2">5+</div>
              <div className="text-secondary-600">Years Experience</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-primary-600 mb-2">
                100%
              </div>
              <div className="text-secondary-600">Client Satisfaction</div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-0 right-0 flex justify-center animate-bounce">
        <button
          onClick={() => scrollToSection("about")}
          className="flex flex-col items-center text-secondary-500 hover:text-primary-600 transition-colors duration-200"
          aria-label="Scroll to next section"
        >
          <span className="text-sm mb-2">Scroll Down</span>
          <ChevronDown className="w-6 h-6" />
        </button>
      </div>
    </section>
  );
}
